import React, { useState } from 'react'
import { Button, Grid, Hidden, makeStyles, Typography } from '@paintscout/ui'
import { useClientOptions, useDialogs, useUser } from '@paintscout/ui-business'
import {
  formatCurrency,
  getPayments,
  getQuoteOptions,
  hasIntegrationInterface,
  hasProcessingPayment,
  PAYMENT_PENDING_MESSAGE
} from '@paintscout/util/builder'
import type { Payment } from 'paintscout'
import { useSnackbar } from 'notistack'
import { track } from 'shared/util/analytics'
import { fetchFreshQuote, makeCheckPaymentBlocked } from '../util/freshQuoteState'
import { useQuote } from '../context'
import { CollectPayrixPaymentDialog } from '../dialogs'
import { openProcessingPaymentWarningDialog } from '../util/openProcessingPaymentWarningDialog'

const CollectStripePaymentDialog = React.lazy(() => import('../dialogs/CollectStripePaymentDialog'))

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    padding: theme.spacing(3, 4),
    background: theme.paintscout.palette.brand.Neutral.P94,
    borderRadius: theme.spacing(1.5),
    width: 954,
    maxWidth: '100%',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: 718
    },
    [theme.breakpoints.down('sm')]: {
      width: '96%'
    }
  },
  mobileMargin: {
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2)
    }
  },
  noteContainer: {
    marginTop: theme.spacing(2)
  },
  ofTotal: {
    color: theme.palette.text.disabled
  }
}))

export interface PaymentRequestProps {
  paymentRequest: Payment
}

export default function PaymentRequest({ paymentRequest }: PaymentRequestProps) {
  const { openDialog, dismissDialog } = useDialogs()
  const { options, clientId } = useClientOptions()
  const { quote, refetchQuote } = useQuote()
  const { enqueueSnackbar } = useSnackbar()
  const [checkingPayment, setCheckingPayment] = useState(false)
  const hasPaymentError = quote.paymentError
  const classes = useStyles()
  const { preferences } = useUser()
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments = hasStripe || hasStripeW1d || hasPayrix

  const { allowPaymentsOnInvoices, allowPaymentsOnQuotes, allowedDepositTypes, allowACHPaymentsOnInvoices } =
    getQuoteOptions({ options, quote })

  const acceptCC = quote.is_invoice
    ? allowPaymentsOnInvoices
    : allowPaymentsOnQuotes && ['credit', 'both'].includes(allowedDepositTypes)

  const acceptACH = quote.is_invoice
    ? allowACHPaymentsOnInvoices
    : allowPaymentsOnQuotes && ['ach', 'both'].includes(allowedDepositTypes)

  const allPayments = getPayments({ quote })
  const activeProcessingPayments = allPayments.filter(
    (p) =>
      p.status === 'processing' &&
      !allPayments.some((other) => other.activityId === p.activityId && other.status === 'paid')
  )
  const processingTotal = activeProcessingPayments.reduce((sum, p) => sum + Math.abs(Number(p.amount)), 0)
  const effectiveAmount = Math.max(0, Math.min(paymentRequest.amount, quote.totals.balance_due - processingTotal))

  const showPayButton = hasPayments && (acceptCC || acceptACH) && effectiveAmount > 0

  const PaymentDialog = hasPayrix ? CollectPayrixPaymentDialog : CollectStripePaymentDialog

  const openPaymentDialog = () => {
    const trackingProps = {
      quoteId: quote._id,
      source: 'payment-request',
      isDeposit: paymentRequest.deposit,
      amount: effectiveAmount,
      processor: hasPayrix ? 'payrix' : 'stripe'
    }

    track('customer_payment_started', trackingProps)

    openDialog(PaymentDialog, {
      quote,
      options,
      companyId: clientId,
      note: paymentRequest.note,
      isDeposit: paymentRequest.deposit,
      amount: effectiveAmount,
      source: 'payment-request',
      paymentRequestId: paymentRequest.id,
      paymentType: acceptCC && acceptACH ? 'both' : acceptACH ? 'ach' : 'credit',
      checkForMismatch: true,
      checkPaymentBlocked: makeCheckPaymentBlocked(refetchQuote),
      onCancel: dismissDialog,
      onConfirm: () => {
        track('customer_payment_completed', trackingProps)
        window.location.reload()
      }
    })
  }

  const handlePay = async () => {
    if (checkingPayment) return
    setCheckingPayment(true)
    // Branch on freshly-fetched state, not page-load state — a prior attempt may have flagged the
    // quote or its payment may still be processing since this page was loaded.
    const fresh = (await fetchFreshQuote(refetchQuote)) ?? quote
    setCheckingPayment(false)
    if (fresh.paymentError) {
      // refetchQuote already pushed the fresh doc into context, so disabled={hasPaymentError} engages.
      enqueueSnackbar(PAYMENT_PENDING_MESSAGE, { variant: 'error' })
      return
    }
    if (hasProcessingPayment({ quote: fresh })) {
      openProcessingPaymentWarningDialog({
        options,
        quote: fresh,
        openDialog,
        dismissDialog,
        onConfirm: openPaymentDialog
      })
    } else {
      openPaymentDialog()
    }
  }

  const PayButton = () =>
    showPayButton && (
      <Button disabled={hasPaymentError} loading={checkingPayment} onClick={handlePay}>
        {acceptACH ? 'Pay Now' : 'Pay by Credit Card'}
      </Button>
    )

  return (
    <Grid container className={classes.root} justifyContent="space-between" alignItems="center">
      <Grid item>
        <Typography variant="h2">Payment Request</Typography>
        <Typography variant="h3">
          {`${formatCurrency({
            options,
            value: paymentRequest?.amount
          })}`}

          <span className={classes.ofTotal}>{` of ${formatCurrency({
            options,
            value: quote.totals?.balance_due
          })}`}</span>
        </Typography>
      </Grid>
      <Hidden xsDown>
        <Grid item className={classes.mobileMargin}>
          <PayButton />
        </Grid>
      </Hidden>
      {!!paymentRequest.note && (
        <Grid item xs={12} className={classes.noteContainer}>
          <Typography>
            <strong>Estimator&lsquo;s Note: </strong>
            {paymentRequest.note}
          </Typography>
        </Grid>
      )}
      <Hidden smUp>
        <Grid item container justifyContent="center">
          <Grid item className={classes.mobileMargin}>
            <PayButton />
          </Grid>
        </Grid>
      </Hidden>
    </Grid>
  )
}
